import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import TracesView from './TracesView';
import ExecutionHistoryView from './ExecutionHistoryView';
import WorkflowService from '../../services/api/workflow';

const WorkflowHistoryDrawer = ({ isOpen, onClose, projectId, projectName, historyData }) => {
  const [view, setView] = useState('traces'); // 'traces' | 'executionHistory'
  const [selectedTrace, setSelectedTrace] = useState(null);
  const [selectedAgentId, setSelectedAgentId] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [tracesCurrentPage, setTracesCurrentPage] = useState(0);
  const [tracesPageSize, setTracesPageSize] = useState(5);
  const [executionMessages, setExecutionMessages] = useState([]);
  const [executionDurationMs, setExecutionDurationMs] = useState(null);
  const [executionDurationsByAgent, setExecutionDurationsByAgent] = useState({});
  const [executionApprovalsByAgent, setExecutionApprovalsByAgent] = useState({});

  const execCacheRef = useRef(new Map()); 
  const inFlightRef = useRef(new Set()); 
  const activeRequestKeyRef = useRef(null);

  const execMessageKey = (m) => {
    if (!m) return '';
    // Prefer stable ids; fallback to a composite key to dedupe across agent fetches.
    return (
      m.id ||
      `${m.event || ''}-${m.createdDate || m.timestamp || ''}-${m.requestId || ''}-${m.agentExecId || ''}-${m.agentId || ''}-${m.pipeId || ''}`
    );
  };

  const mergeExecMessages = (baseList, nextList) => {
    const base = Array.isArray(baseList) ? baseList : [];
    const incoming = Array.isArray(nextList) ? nextList : [];
    if (!base.length) return incoming;
    if (!incoming.length) return base;

    const seen = new Set(base.map(execMessageKey));
    const merged = base.slice();
    for (const m of incoming) {
      const k = execMessageKey(m);
      if (!k || seen.has(k)) continue;
      seen.add(k);
      merged.push(m);
    }
    return merged;
  };

  const pickResponseApproval = (payload) => {
    const approval = payload?.approval;
    if (approval == null || typeof approval !== 'object' || Array.isArray(approval)) return null;
    const hasValue = Object.values(approval).some((v) => v != null && String(v).trim() !== '');
    return hasValue ? approval : null;
  };

  useEffect(() => {
    if (!isOpen) return;
    setView('traces');
    setSelectedTrace(null);
    setSelectedAgentId(null);
    setExecutionMessages([]);
    setExecutionDurationMs(null);
    setExecutionDurationsByAgent({});
    setExecutionApprovalsByAgent({});
    setIsRefreshing(false);
    setTracesCurrentPage(0);
    setTracesPageSize(5);
    execCacheRef.current = new Map();
    inFlightRef.current = new Set();
    activeRequestKeyRef.current = null;
  }, [isOpen]);

  const clearExecutionIdFromUrl = useMemo(() => {
    return () => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.delete('executionId');
        return next;
      }, { replace: true });
    };
  }, [setSearchParams]);

  const resolveFirstAgentIdFromTrace = useMemo(() => {
    return (trace) => {
      if (!trace) return null;

      const list = trace?.appConfig?.agentConfig ?? trace?.agentConfig ?? [];
      if (Array.isArray(list)) {
        const first = list.find((c) => c?.agentId);
        if (first?.agentId) return first.agentId;
      }

      // Fallback: sometimes agent ids live on nodes (agent/task).
      const nodes = Array.isArray(trace?.nodes) ? trace.nodes : [];
      for (const n of nodes) {
        if (!n) continue;
        const type = String(n?.type || '').toLowerCase();
        if (type !== 'agent' && type !== 'task') continue;
        const id =
          n?.data?.agentId ||
          n?.data?.agent_id ||
          n?.agentId ||
          n?.agent_id ||
          null;
        if (id) return id;
      }

      return null;
    };
  }, []);

  const handleOpenExecutionHistoryPage = (trace) => {
    setSelectedTrace(trace || null);
    const firstAgentId = resolveFirstAgentIdFromTrace(trace);
    setSelectedAgentId(firstAgentId);
    setExecutionMessages([]);
    setExecutionDurationMs(null);
    setExecutionDurationsByAgent({});
    setExecutionApprovalsByAgent({});
    setView('executionHistory');
  };

  // On entering execution history, auto-load the first agent once so tabs populate by default.
  useEffect(() => {
    if (view !== 'executionHistory') return;
    if (!selectedTrace || !selectedAgentId) return;
    const executionId =
      selectedTrace?.id ||
      selectedTrace?.executionId ||
      selectedTrace?.workflowExecutionId ||
      null;
    if (!executionId) return;
    const key = `${executionId}::${selectedAgentId}`;
    const hasMsgs = Array.isArray(executionMessages) && executionMessages.length > 0;
    if (hasMsgs) return;
    if (execCacheRef.current.has(key)) return;
    if (inFlightRef.current.has(key) || activeRequestKeyRef.current === key) return;
    handleViewExecutionHistory(
      selectedTrace,
      { attributes: { agentId: selectedAgentId } },
      { showRefreshing: true }
    );
  }, [view, selectedTrace, selectedAgentId]);

  const resolveAgentIdFromNode = (agentNode) => {
    if (!agentNode) return null;
    const attrs = agentNode?.attributes || {};
    const nodeId = typeof agentNode?.id === 'string' ? agentNode.id : '';
    const parsedFromNodeId =
      nodeId.startsWith('agent:') ? nodeId.split('agent:')[1]?.split('::')[0]?.trim() : null;
    return (
      attrs.agentId ||
      attrs.agent_id ||
      attrs?.agentId?.agentId ||
      agentNode?.agentId ||
      parsedFromNodeId ||
      null
    );
  };

  const handleViewExecutionHistory = async (trace, agentNode, { force = false, showRefreshing = false } = {}) => {
    setSelectedTrace(trace || null);
    let cacheKey = null;
    if (showRefreshing) setIsRefreshing(true);

    if (!trace) {
      setExecutionMessages([]);
      setExecutionDurationMs(null);
      setExecutionDurationsByAgent({});
      setExecutionApprovalsByAgent({});
      setSelectedAgentId(null);
      setView('executionHistory');
      if (showRefreshing) setIsRefreshing(false);
      return;
    }

    try {
      const executionId =
        trace?.id ||
        trace?.executionId ||
        trace?.workflowExecutionId ||
        null;
      const clickedAgentId = resolveAgentIdFromNode(agentNode);
      const agentIdToFetch = clickedAgentId || selectedAgentId;

      if (!executionId || !agentIdToFetch) {
        console.warn('Missing executionId or agentId for trace, cannot load execution history', {
          executionId,
          agentIdToFetch,
          trace
        });
        setExecutionMessages([]);
        setExecutionDurationsByAgent({});
        if (showRefreshing) setIsRefreshing(false);
        return;
      }

      cacheKey = `${executionId}::${agentIdToFetch}`;
      const cached = execCacheRef.current.get(cacheKey);
      if (!force && cached) {
        setExecutionMessages((prev) => mergeExecMessages(prev, cached.messages || []));
        setSelectedAgentId(agentIdToFetch);
        setExecutionDurationsByAgent((prev) => ({
          ...(prev || {}),
          ...(typeof cached.durationMs === 'number' ? { [agentIdToFetch]: cached.durationMs } : {})
        }));
        setExecutionDurationMs(typeof cached.durationMs === 'number' ? cached.durationMs : null);
        setExecutionApprovalsByAgent((prev) => ({
          ...(prev || {}),
          [agentIdToFetch]: cached.approval ?? null
        }));
        if (showRefreshing) setIsRefreshing(false);
        return;
      }

      if (!force && (inFlightRef.current.has(cacheKey) || activeRequestKeyRef.current === cacheKey)) {
        if (showRefreshing) setIsRefreshing(false);
        return;
      }

      if (inFlightRef.current.has(cacheKey)) {
        if (!force) return;
        inFlightRef.current.delete(cacheKey);
      }
      if (force) execCacheRef.current.delete(cacheKey);
      inFlightRef.current.add(cacheKey);
      activeRequestKeyRef.current = cacheKey;

      const r = await WorkflowService.getWorkflowExecution(executionId, agentIdToFetch, 0, 100).catch(() => null);
      const data = r && r.data && !Array.isArray(r.data) && (r.data.execMsgDTOS || r.data.approval)
        ? r.data
        : r;
      const execMsgs = Array.isArray(data) ? data : data?.execMsgDTOS || [];
      const durationMs = data?.duration?.durationInMillis ?? r?.duration?.durationInMillis ?? null;
      const approval = pickResponseApproval(data) || pickResponseApproval(r);

      execCacheRef.current.set(cacheKey, {
        messages: execMsgs || [],
        durationMs: typeof durationMs === 'number' ? durationMs : null,
        approval
      });
      setExecutionMessages((prev) => mergeExecMessages(prev, execMsgs || []));
      setSelectedAgentId(agentIdToFetch);
      setExecutionApprovalsByAgent((prev) => ({
        ...(prev || {}),
        [agentIdToFetch]: approval
      }));
      if (typeof durationMs === 'number') {
        setExecutionDurationsByAgent((prev) => ({ ...(prev || {}), [agentIdToFetch]: durationMs }));
        setExecutionDurationMs(durationMs);
      } else {
        setExecutionDurationMs(null);
      }
    } catch (error) {
      console.error('Error fetching workflow execution history:', error);
      setExecutionMessages([]);
      setExecutionDurationMs(null);
      setExecutionDurationsByAgent({});
      setExecutionApprovalsByAgent({});
    } finally {
      if (cacheKey) inFlightRef.current.delete(cacheKey);
      if (cacheKey && activeRequestKeyRef.current === cacheKey) {
        activeRequestKeyRef.current = null;
      }
      if (showRefreshing) setIsRefreshing(false);
      setView('executionHistory');
    }
  };

  if (!isOpen) return null;
  if (view === 'traces') {
    return (
      <>
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            width: '100%',
            height: '100vh',
            backgroundColor: '#ffffff',
            zIndex: 2000,
            animation: 'fadeIn 0.2s ease-out'
          }}
        >
          <TracesView
            projectId={projectId}
            projectName={projectName}
            historyData={historyData}
            currentPage={tracesCurrentPage}
            pageSize={tracesPageSize}
            setCurrentPage={setTracesCurrentPage}
            setPageSize={setTracesPageSize}
            onViewExecutionHistory={handleOpenExecutionHistoryPage}
            onClose={onClose}
          />
        </div>
        <style>{`
          @keyframes fadeIn {
            from {
              opacity: 0;
            }
            to {
              opacity: 1;
            }
          }
        `}</style>
      </>
    );
  }

  if (view === 'executionHistory') {
    return (
      <ExecutionHistoryView
        trace={selectedTrace}
        execMessages={executionMessages || []}
        isRefreshing={isRefreshing}
        executionDurationMs={executionDurationMs}
        executionDurationsByAgent={executionDurationsByAgent}
        executionApprovalsByAgent={executionApprovalsByAgent}
        onBack={() => {
          clearExecutionIdFromUrl();
          setView('traces');
        }}
        onClose={() => {
          clearExecutionIdFromUrl();
          setView('traces');
        }}
        onRefresh={(agentIdFromTree, refreshOptions = {}) => {
          if (!selectedTrace) return;
          const aid =
            agentIdFromTree ||
            selectedAgentId ||
            null;
          if (!aid) return;
          handleViewExecutionHistory(
            selectedTrace,
            { attributes: { agentId: aid } },
            {
              force: true,
              showRefreshing: refreshOptions.silent ? false : true
            }
          );
        }}
        onAgentClick={(agentNode) => {
          if (selectedTrace) {
            handleViewExecutionHistory(selectedTrace, agentNode);
          }
        }}
        selectedAgentId={selectedAgentId}
      />
    );
  }

  return null;
};

export default WorkflowHistoryDrawer;

